// Web Push opt-in (v1 semantics carried exactly): a device subscribes itself, keeps
// the server's copy fresh on every boot, and if the VAPID key ever rotates it quietly
// re-subscribes under the new key — the old subscription can never be delivered to.
import { signal } from '@preact/signals';
import { apiPost } from './api';

/** Served by /api/whoami; empty until the boot fetch lands (or when push is unset). */
export const vapidPublic = signal('');

/** True when this device could subscribe but hasn't yet (drives the button). */
export const canEnablePush = signal(false);

function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

function urlB64(b64: string): Uint8Array {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const a = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) a[i] = raw.charCodeAt(i);
  return a;
}

function keyToB64u(buf: ArrayBuffer): string {
  const a = new Uint8Array(buf);
  let s = '';
  for (const b of a) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function registerSub(sub: PushSubscription, name: string): Promise<void> {
  await apiPost('/api/subscribe', { sub: sub.toJSON(), name });
}

export async function refreshCanEnable(): Promise<void> {
  if (!pushSupported() || !vapidPublic.value || Notification.permission === 'denied') {
    canEnablePush.value = false;
    return;
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    canEnablePush.value = !(await reg.pushManager.getSubscription());
  } catch {
    canEnablePush.value = false;
  }
}

/** User-gesture path: ask permission, subscribe, tell the server. Returns a toastable
 *  message rather than throwing — callers just show it. */
export async function enablePush(name: string): Promise<string> {
  if (!pushSupported() || !vapidPublic.value) return 'Alerts not available yet.';
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    void refreshCanEnable();
    return 'Allow notifications to get alerts.';
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64(vapidPublic.value) as BufferSource,
    });
    await registerSub(sub, name);
    void refreshCanEnable();
    return 'Alerts on.';
  } catch {
    void refreshCanEnable();
    return 'Could not enable alerts.';
  }
}

/** Boot path: refresh the server's copy of an existing subscription; on VAPID key
 *  rotation, re-subscribe under the new key. Silent — never bothers the user. */
export async function syncPushSub(name: string): Promise<void> {
  if (!pushSupported() || !vapidPublic.value) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) {
      void refreshCanEnable();
      return;
    }
    const key = sub.options.applicationServerKey;
    if (key && keyToB64u(key as ArrayBuffer) !== vapidPublic.value) {
      await sub.unsubscribe();
      const fresh = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64(vapidPublic.value) as BufferSource,
      });
      await registerSub(fresh, name);
    } else {
      await registerSub(sub, name);
    }
    void refreshCanEnable();
  } catch {
    // best-effort; the enable button remains as the manual path
  }
}
